<template>
  <div class="min-h-screen">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="DevOps & Deployment Services - Infrastructure Solutions"
      description="Infrastructure setup, deployment automation, and continuous integration for reliable application delivery. We ensure your applications are deployed securely and scale efficiently."
      :keywords="['DevOps', 'deployment', 'CI/CD', 'Docker', 'AWS', 'cloud infrastructure', 'automation', 'scalable deployment']"
      url="https://vedware.com/services/devops-deployment"
    />

    <!-- Hero Section -->
    <HeroSection
      :icon="PhCloud"
      title="DevOps &"
      highlighted-title="Deployment"
      description="Infrastructure setup, deployment automation, and continuous integration for reliable application delivery. We ensure your applications are deployed securely and scale efficiently."
    />

    <!-- Service Overview -->
    <ServiceOverview
      :description="[
        'Our DevOps and deployment service focuses on creating reliable, scalable, and automated infrastructure that supports your applications throughout their lifecycle.',
        'From containerization to cloud deployment, we implement modern DevOps practices that ensure fast, reliable deployments and optimal application performance in production environments.',
      ]"
      :features="[
        'Automated CI/CD pipelines',
        'Container orchestration and management',
        'Cloud infrastructure setup and management',
        'Monitoring and logging solutions',
        'Security and compliance implementation',
        'Disaster recovery and backup strategies',
      ]"
    />

    <!-- Technologies Section -->
    <ServiceTechnologies
      title="DevOps Technologies"
      description="We use industry-leading DevOps tools and technologies to build reliable, scalable infrastructure."
      :categories="technologyCategories"
    />

    <!-- Services Offered -->
    <ServiceOfferings
      title="DevOps Services"
      description="We provide comprehensive DevOps and deployment services to streamline your development workflow."
      :services="devopsServices"
    />

    <ServiceProjects slug="devops-deployment" />

    <!-- CTA Section -->
    <ServiceCTA
      title="Ready to Streamline Your"
      highlighted-title="Deployment?"
      description="Let's implement modern DevOps practices that will accelerate your development and ensure reliable deployments."
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import {
  PhActivity,
  PhArrowClockwise,
  PhCloud,
  PhGitBranch,
  PhPackage,
  PhShield,
} from '@phosphor-icons/vue'
import ServiceCTA from '~/components/Services/ServiceCTA.vue'
import ServiceOfferings from '~/components/Services/ServiceOfferings.vue'
import ServiceOverview from '~/components/Services/ServiceOverview.vue'
import ServiceProjects from '~/components/Services/ServiceProjects.vue'
import ServiceTechnologies from '~/components/Services/ServiceTechnologies.vue'
import HeroSection from '~/components/Shared/HeroSection.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'

const technologyCategories = ref([
  {
    title: 'Containerization',
    icon: PhPackage,
    technologies: [
      'Docker',
      'Docker Compose',
      'Docker Swarm',
      'Kubernetes',
      'Container Orchestration',
      'Microservices',
    ],
  },
  {
    title: 'Cloud Platforms',
    icon: PhCloud,
    technologies: [
      'AWS',
      'Amazon EC2',
      'Amazon S3',
      'Amazon RDS',
      'AWS Lambda',
      'Linode',
      'Cloud Infrastructure',
      'Load Balancers',
    ],
  },
  {
    title: 'CI/CD & Monitoring',
    icon: PhActivity,
    technologies: [
      'CI/CD Pipelines',
      'GitHub Actions',
      'Travis CI',
      'Jenkins',
      'Git',
      'Automated Testing',
      'Deployment Automation',
    ],
  },
])

const devopsServices = ref([
  {
    icon: PhGitBranch,
    title: 'CI/CD Pipeline Setup',
    description: 'Automated build, test, and deployment pipelines that ensure code quality and fast, reliable releases.',
  },
  {
    icon: PhPackage,
    title: 'Containerization',
    description: 'Docker containerization and orchestration for consistent, scalable application deployment across environments.',
  },
  {
    icon: PhCloud,
    title: 'Cloud Infrastructure',
    description: 'Cloud platform setup and management, including server provisioning, networking, and security configuration.',
  },
  {
    icon: PhActivity,
    title: 'Monitoring & Logging',
    description: 'Comprehensive monitoring, logging, and alerting systems to ensure optimal application performance and reliability.',
  },
  {
    icon: PhShield,
    title: 'Security & Compliance',
    description: 'Security hardening, compliance implementation, and best practices for secure infrastructure and deployments.',
  },
  {
    icon: PhArrowClockwise,
    title: 'Disaster Recovery',
    description: 'Backup strategies, disaster recovery planning, and business continuity solutions for mission-critical applications.',
  },
])
</script>
